// Batch 27 · Add-on 3 · D — the "Subsequent journals" source of the unrecorded-liabilities worksheet (<head>-07), route 2.
// Its own file (big-file rule) and its own EN + AR text. Upload the post-year-end journals (GL import format) or read the FY+1
// engagement's GL; vouchers grouped; candidates / all; lines expandable; reversal of a recorded accrual shown and excluded;
// relates (proposed only from a date on or before the period end), match, result, omitted, documents, misstatement.
import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useState } from "react";
import { SurlVatFields, SurlLinkActions } from "@/components/SurlRowB34";   // Batch 34 · Z-15 / Z-16
import api, { toErrMsg } from "@/lib/api";
import { waitForJob } from "@/lib/waitJob";
import { fmtNumber, fmtCount } from "@/components/Amount";
import { fmtDate } from "@/lib/dt";
import { asArray } from "@/lib/asArray";
import { toast } from "sonner";
import TableSaveBar, { RowSaveError } from "@/components/TableSaveBar";   // Batch 28 · Follow-up 1 · F2 (b)
import { useTableDraft } from "@/lib/useDrafts";
import { saveRowFirst } from "@/lib/saveThenSend";   // STEP 11d · A9: Send saves a changed row first
import RecordedYe from "@/components/SurlRecordedYe";   // Batch 29 · M64 (c)
import { offerLink } from "@/lib/misstatementDuplicate";   // Batch 29 · M65
import { LinkedDocsViewButton } from "@/components/VouchingDocViewer";   // Batch 34 · B34-11
import { AnswerMissingNote } from "@/components/SurlAgingB34L";   // Batch 34 · S18
import { jobErrorText } from "@/lib/jobErrorText";   // Step 11d · A2: a failed job's error in the screen's language

const fmt = (n) => fmtNumber(Number(n || 0));

/** Batch 30 · M75: "45 lines / 11 vouchers in the file · 43 lines / 10 vouchers in the window" — both counts on the same
 *  basis (before: every line of the file next to the vouchers of the window only). Uploads stored before Batch 30 have no
 *  file voucher count: "45 lines in the file · 43 lines / 10 vouchers in the window". */
export function countsLine(jr, J) {
  const inWin = jr.lines_in_window ?? jr.counts?.in_window;
  const file = jr.vouchers_in_file != null
    ? `${jr.lines_in_file} ${J.lines} / ${jr.vouchers_in_file} ${J.vouchers} ${J.inFile}`
    : `${jr.lines_in_file} ${J.lines} ${J.inFile}`;
  return inWin != null ? `${file} · ${inWin} ${J.lines} / ${jr.voucher_count} ${J.vouchers} ${J.inWindow}` : file;
}

export const JTXT = {
  en: {
    tabPayments: "Payments & invoices", tabJournals: "Subsequent journals",
    upload: "Upload the post-year-end journals", uploadCols: "GL import format: Posting Date, Voucher, Account Code, Account Name, Debit, Credit, Narration, Party, Entered by, Time (.xlsx / .csv). Kept on this worksheet only — never added to this year's ledger.",
    fy1: "Use the FY+1 engagement's GL", fy1Done: "Read from the FY+1 engagement", uploaded: "Loaded", lines: "lines", vouchers: "vouchers", inFile: "in the file", inWindow: "in the window",
    candidatesOnly: "Candidates only", all: "All vouchers", noRows: "Upload the journals or read the FY+1 engagement's GL to start.",
    cols: { voucher: "Voucher", date: "Date", lines: "Lines", accounts: "Accounts Dr / Cr", amount: "Amount", narration: "Narration",
            relates: "Relates to the period?", match: "Match / reversal", result: "Result", omitted: "Omitted", docs: "Document", ms: "Misstatement" },
    show: "Show", hide: "Hide", dr: "Dr", cr: "Cr",
    rel: { "": "To decide", yes: "Yes", no: "No", part: "Part" }, proposed: "Proposed", partAmount: "Part relating to the period",
    reversal: "Reversal of recorded accrual", fromAging: "AP aging line", fromGl: "Year-end accrual in the GL", accept: "Accept", reject: "Reject",
    res: { recorded: "Recorded", omitted: "Omitted", next_period: "Relates to next period", part_omitted: "Part omitted", to_confirm: "To confirm",
           not_selected: "—", reversal: "Reversal — excluded", covered_route1: "Covered by route 1", recorded_accrual: "Recorded (accrual)" },
    fromAccrual: "Year-end accrual", accrualHow: { narration: "named in the narration", settlement: "settled (accrual account debited)",
                                                   rebooking: "re-booked after reversal {v}" },
    sameAs: "Same item as route 1 row…", sameAsPick: "Choose the route 1 row", sameAsLink: "Link", sameAsUnlink: "Unlink",
    sameAsProposed: "Proposed: same party and amount", coveredBy: "Covered by route 1", linkLost: "The linked route 1 row is no longer omitted or was re-uploaded — link again or send this journal",
    tCovered: "Covered by route 1",
    send: "Send to Misstatements", sendAgain: "Send again (refresh)", sent: "Sent",
    totals: "Subsequent journals — summary", combined: "Both sources — combined", tSelected: "Selected", tReversal: "Reversals excluded",
    tOmitted: "Omitted", tNext: "Next period", tConfirm: "To confirm", tSent: "Sent",
    routeNotPerformed: "This route was not performed", reason: "Reason", saveReason: "Save", performed: "Performed",
    accrualWindow: "Year-end accruals a journal may reverse: entries that record a liability, dated in the last {n} days before the period end (from {from}) or marked as an accrual / provision",
    accrualDays: "Days", notChecked: "{n} ledger lines were not checked for reversals (limit reached)",
  },
  ar: {
    tabPayments: "المدفوعات والفواتير", tabJournals: "القيود اللاحقة",
    upload: "ارفع قيود ما بعد نهاية السنة", uploadCols: "بصيغة استيراد دفتر الأستاذ: تاريخ الترحيل، القيد، رمز الحساب، اسم الحساب، مدين، دائن، البيان، الطرف، أدخله، الوقت (.xlsx / .csv). تُحفظ في ورقة العمل هذه فقط — ولا تُضاف أبداً إلى دفتر أستاذ هذه السنة.",
    fy1: "استخدم دفتر أستاذ ارتباط السنة التالية", fy1Done: "قُرئ من ارتباط السنة التالية", uploaded: "حُمّل", lines: "سطور", vouchers: "قيود", inFile: "في الملف", inWindow: "ضمن الفترة",
    candidatesOnly: "المرشحة فقط", all: "كل القيود", noRows: "ارفع القيود أو اقرأ دفتر أستاذ ارتباط السنة التالية للبدء.",
    cols: { voucher: "القيد", date: "التاريخ", lines: "السطور", accounts: "الحسابات مدين / دائن", amount: "المبلغ", narration: "البيان",
            relates: "يخص الفترة؟", match: "المطابقة / العكس", result: "النتيجة", omitted: "المحذوف", docs: "المستند", ms: "التحريف" },
    show: "إظهار", hide: "إخفاء", dr: "مدين", cr: "دائن",
    rel: { "": "للتحديد", yes: "نعم", no: "لا", part: "جزء" }, proposed: "مقترح", partAmount: "الجزء الذي يخص الفترة",
    reversal: "عكس استحقاق مسجل", fromAging: "سطر أعمار الذمم الدائنة", fromGl: "استحقاق نهاية السنة في دفتر الأستاذ", accept: "قبول", reject: "رفض",
    res: { recorded: "مسجل", omitted: "محذوف", next_period: "يخص الفترة التالية", part_omitted: "محذوف جزئياً", to_confirm: "للتأكيد",
           not_selected: "—", reversal: "عكس — مستبعد", covered_route1: "مغطى بالطريقة 1", recorded_accrual: "مسجل (استحقاق)" },
    fromAccrual: "استحقاق نهاية السنة", accrualHow: { narration: "مذكور في البيان", settlement: "مسدد (مدين حساب الاستحقاق)",
                                                      rebooking: "أعيد تسجيله بعد العكس {v}" },
    sameAs: "البند نفسه في صف الطريقة 1…", sameAsPick: "اختر صف الطريقة 1", sameAsLink: "ربط", sameAsUnlink: "إلغاء الربط",
    sameAsProposed: "مقترح: الطرف والمبلغ نفسهما", coveredBy: "مغطى بالطريقة 1", linkLost: "صف الطريقة 1 المرتبط لم يعد محذوفاً أو أُعيد رفعه — اربطه مجدداً أو أرسل هذا القيد",
    tCovered: "مغطى بالطريقة 1",
    send: "أرسل إلى التحريفات", sendAgain: "أرسل مجدداً (تحديث)", sent: "أُرسل",
    totals: "القيود اللاحقة — الملخص", combined: "المصدران — مجتمعان", tSelected: "المختارة", tReversal: "العكوس المستبعدة",
    tOmitted: "المحذوف", tNext: "الفترة التالية", tConfirm: "للتأكيد", tSent: "المُرسل",
    routeNotPerformed: "لم تُنفَّذ هذه الطريقة", reason: "السبب", saveReason: "حفظ", performed: "منفذة",
    accrualWindow: "استحقاقات نهاية السنة التي قد يعكسها قيد: القيود التي تسجل التزاماً، المؤرخة في آخر {n} يوماً قبل نهاية الفترة (من {from}) أو المعلّمة كاستحقاق / مخصص",
    accrualDays: "الأيام", notChecked: "{n} من سطور دفتر الأستاذ لم تُفحص للعكس (بلغ الحد)",
  },
};

/** "This route was not performed" + reason, for either source (the gate needs one performed, the other performed or explained). */
export function RouteToggle({ engId, code, route, state, J, done, onSaved, mf }) {
  const [on, setOn] = useState(!!state?.not_performed);
  const [reason, setReason] = useState(state?.reason || "");
  if (state?.performed) return <p className="text-[11px] text-emerald-700" data-testid={`surl-route-${route}-performed`}>{J.performed}</p>;
  const save = async (np, why) => {
    try {
      await api.patch(`/engagements/${engId}/surl/${code}/routes`, { route, not_performed: np, reason: why });
      if (mf) mf.clear(`routes.${route}.reason`);
      onSaved && onSaved();
    } catch (e) { if (!(mf && mf.apply(e))) toast.error(toErrMsg(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs" data-testid={`surl-route-${route}`}>
      <label className="flex items-center gap-1">
        <input type="checkbox" checked={on} disabled={done} data-testid={`surl-route-${route}-np`}
               onChange={(e) => { setOn(e.target.checked); if (!e.target.checked) save(false, ""); }} /> {J.routeNotPerformed}
      </label>
      {on && (<>
        <input type="text" value={reason} disabled={done} placeholder={J.reason} data-testid={`surl-route-${route}-reason`}
               onChange={(e) => setReason(e.target.value)}
               {...(mf ? mf.props(`routes.${route}.reason`, "min-w-0 flex-1 rounded border px-2 py-1 text-xs") : { className: "min-w-0 flex-1 rounded border px-2 py-1 text-xs" })} />
        <button type="button" disabled={done} className="rounded border px-2 py-1" data-testid={`surl-route-${route}-save`}
                onClick={() => save(true, reason)}>{J.saveReason}</button>
      </>)}
    </div>
  );
}

function Lines({ v, J }) {
  return (
    <table className="mt-1 w-full text-[11px]" data-testid={`surl-j-lines-${v.row_id}`}>
      <tbody>{asArray(v.lines).map((l, i) => (
        <tr key={i} className="border-t">
          <td className="px-1 font-mono">{l.account_code}</td><td className="px-1">{l.account_name}</td>
          <td className="px-1 text-end tabular-nums">{l.debit ? `${J.dr} ${fmt(l.debit)}` : ""}</td>
          <td className="px-1 text-end tabular-nums">{l.credit ? `${J.cr} ${fmt(l.credit)}` : ""}</td>
          <td className="px-1">{l.narration}</td><td className="px-1">{l.party}</td><td className="px-1">{l.entered_by}</td>
        </tr>))}</tbody>
    </table>
  );
}

// Batch 28 · item 2 (M44 + M48): "Same item as route 1 row …" — the liability is counted and sent once, from route 1
export function SameItem({ v, J, done, busy, r1rows, link, unlink }) {
  const ev = v.eval || {};
  const prop = ev.same_as_proposal || null;
  const [pick, setPick] = useState(prop?.row_id || "");
  if (ev.result === "covered_route1") {
    const c = ev.covered_by || {};
    return (
      <div className="mt-1 text-[11px]" data-testid={`surl-j-covered-${v.row_id}`}>
        {J.coveredBy}: {c.party} · {fmt(c.omitted_amount)}
        {!done && <button type="button" className="ms-1 underline" disabled={busy} onClick={() => unlink(v.row_id)}
                          data-testid={`surl-j-unlink-${v.row_id}`}>{J.sameAsUnlink}</button>}
      </div>
    );
  }
  if (!["omitted", "part_omitted"].includes(ev.result) || !(ev.omitted_amount > 0) || v.misstatement_id || !r1rows.length) {
    return ev.link_lost ? <div className="mt-1 text-[11px] text-amber-700" data-testid={`surl-j-link-lost-${v.row_id}`}>{J.linkLost}</div> : null;
  }
  return (
    <div className="mt-1 space-y-1 text-[11px]" data-testid={`surl-j-sameas-${v.row_id}`}>
      {ev.link_lost && <div className="text-amber-700" data-testid={`surl-j-link-lost-${v.row_id}`}>{J.linkLost}</div>}
      <div className="text-muted-foreground">{J.sameAs}</div>
      <select value={pick} disabled={done || busy} onChange={(e) => setPick(e.target.value)} className="max-w-[14rem] rounded border px-1 py-0.5 text-xs"
              data-testid={`surl-j-sameas-pick-${v.row_id}`}>
        <option value="">{J.sameAsPick}</option>
        {r1rows.map((r) => <option key={r.row_id} value={r.row_id}>{r.party || "—"} · {r.invoice_no || r.voucher || ""} · {fmt(r.eval?.omitted_amount)}</option>)}
      </select>
      {prop && pick === prop.row_id && <div className="text-sky-700" data-testid={`surl-j-sameas-proposed-${v.row_id}`}>{J.sameAsProposed}</div>}
      <button type="button" className="rounded border px-2 py-0.5" disabled={done || busy || !pick} onClick={() => link(v.row_id, pick)}
              data-testid={`surl-j-sameas-link-${v.row_id}`}>{J.sameAsLink}</button>
    </div>
  );
}

function VoucherRow({ v: v0, J, done, busy, patch, send, openLink, mf, r1rows, link, unlink, td, engId, code, refresh }) {
  const [open, setOpen] = useState(false);
  const v = td.rowValue(v0);
  const ev = v.eval || {};
  const m = ev.reversal || ev.proposed_match;
  const rel = v.relates || "";
  return (
    <tr className={`border-t align-top ${td.rowClass(v.row_id)}`} data-testid={`surl-j-row-${v.row_id}`}
        data-changed={td.isChanged(v.row_id) ? "1" : "0"} data-failed={td.errorOf(v.row_id) ? "1" : "0"}>
      <td className="px-2 py-1 font-mono">{v.voucher}<RowSaveError draft={td} id={v.row_id} testid={`surl-j-row-error-${v.row_id}`} /></td>
      <td className="px-2 py-1 whitespace-nowrap">{fmtDate(v.date)}</td>
      <td className="px-2 py-1">
        <button type="button" className="underline" onClick={() => setOpen((o) => !o)} data-testid={`surl-j-toggle-${v.row_id}`}>
          {asArray(v.lines).length} · {open ? J.hide : J.show}</button>
        {open && <Lines v={v} J={J} />}
      </td>
      <td className="px-2 py-1 font-mono text-[11px]">{asArray(ev.accounts_dr).join(", ")} / {asArray(ev.accounts_cr).join(", ")}</td>
      <td className="px-2 py-1 text-end tabular-nums">{fmt(v.amount)}</td>
      <td className="px-2 py-1">{v.narration}</td>
      <td className="px-2 py-1">
        {ev.selected && ev.result !== "reversal" && (<>
          <select value={rel} disabled={done || busy} data-testid={`surl-j-relates-${v.row_id}`} className="rounded border px-1 py-0.5 text-xs"
                  onChange={(e) => td.edit(v.row_id, "relates", e.target.value)}>
            {["", "yes", "no", "part"].map((k) => <option key={k} value={k}>{k === "" ? `${J.proposed}: ${J.rel[ev.proposed_relates || ""]}` : J.rel[k]}</option>)}
          </select>
          <input type="number" step="0.01" placeholder={J.partAmount} value={v.part_amount ?? ""} disabled={done || busy}
                 data-testid={`surl-j-part-${v.row_id}`}
                 {...(mf ? mf.props(`part_amount.${v.row_id}`, "mt-1 block w-28 rounded border px-1 py-0.5 text-xs") : { className: "mt-1 block w-28 rounded border px-1 py-0.5 text-xs" })}
                 onChange={(e) => { td.edit(v.row_id, "part_amount", e.target.value === "" ? "" : Number(e.target.value)); if (e.target.value !== "") td.edit(v.row_id, "relates", "part"); }} />
        </>)}
      </td>
      <td className="px-2 py-1 text-[11px]" data-testid={`surl-j-match-${v.row_id}`}>
        {m && (<div>
          {ev.reversal ? `${J.reversal} ${ev.reversal.voucher} · ${fmtDate(ev.reversal.date)}`
            : m.source === "accrual" ? `${J.fromAccrual} ${m.voucher} · ${fmtDate(m.date)} — ${(J.accrualHow[m.how] || "").replace("{v}", m.reversed_by || "")}`
            : (m.source === "aging" ? J.fromAging : J.fromGl)}
          {m.amount != null && ` · ${fmt(m.amount)}`}
          {ev.selected && !done && (<span className="ms-1">
            <button type="button" className="underline" disabled={busy} onClick={() => patch(v.row_id, { match_decision: "accept" })}>{J.accept}</button>{" "}
            <button type="button" className="underline" disabled={busy} onClick={() => patch(v.row_id, { match_decision: "reject" })}
                    data-testid={`surl-j-reject-${v.row_id}`}>{J.reject}</button></span>)}
        </div>)}
      </td>
      <td className="px-2 py-1">
        <span data-testid={`surl-j-result-${v.row_id}`}>{ev.recorded_how === "accrual" ? J.res.recorded_accrual : (J.res[ev.result] || ev.result)}</span>
        <AnswerMissingNote ev={ev} testid={`surl-j-answer-missing-${v.row_id}`} />
        <RecordedYe ev={ev} ref_={v.recorded_ye_ref} lang={J === JTXT.ar ? "ar" : "en"} disabled={done || busy} testid={`surl-j-rye-${v.row_id}`}
                    onAnswer={(ref) => patch(v.row_id, { recorded_ye_ref: ref })} />
        <SameItem v={v} J={J} done={done} busy={busy} r1rows={r1rows} link={link} unlink={unlink} />
      </td>
      <td className="px-2 py-1 text-end tabular-nums" data-testid={`surl-j-omitted-${v.row_id}`}>{ev.omitted_amount ? fmt(ev.omitted_amount) : ""}</td>
      <td className="px-2 py-1"><button type="button" className="text-xs underline" onClick={openLink} disabled={done}>
        {asArray(v.document_ids).length || "+"}</button>
        <LinkedDocsViewButton engId={engId} docIds={v.document_ids} label={v.voucher} testId={`surl-j-docs-view-${v.row_id}`} /></td>
      <td className="px-2 py-1">
        {["omitted", "part_omitted"].includes(ev.result) && ev.omitted_amount > 0 && (
          <button type="button" disabled={done || busy} className="rounded border px-2 py-0.5 text-xs" data-testid={`surl-j-send-${v.row_id}`}
                  onClick={() => send(v.row_id)}>{v.misstatement_id ? (ev.needs_refresh ? J.sendAgain : J.sent) : J.send}</button>)}
        {["omitted", "part_omitted"].includes(ev.result) && ev.omitted_amount > 0 && engId && (   /* Batch 34 · Z-16 */
          <SurlLinkActions engId={engId} code={code} r={v} type="surl_journal" dis={done || busy} onDone={refresh} />)}
        <SurlVatFields r={v} ev={ev} dis patchRow={() => {}} />
      </td>
    </tr>
  );
}

function Totals({ t, title, J, testid }) {
  const cells = [["tSelected", "selected"], ["tReversal", "reversal"], ["tCovered", "covered"], ["tOmitted", "omitted"], ["tNext", "next_period", true],
                 ["tConfirm", "to_confirm", true], ["tSent", "sent"]];
  return (
    <div className="rounded border p-3" data-testid={testid}>
      <div className="mb-2 font-medium">{title}</div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-7">
        {cells.filter(([, k]) => t[`${k}_count`] !== undefined).map(([lab, k, countOnly]) => (
          <div key={k} data-testid={`${testid}-${k}`}>
            <div className="text-xs text-muted-foreground">{J[lab]}</div>
            <div className="tabular-nums">{t[`${k}_count`] ?? 0}{countOnly ? "" : ` · ${fmt(t[`${k}_amount`])}`}</div>
          </div>))}
      </div>
    </div>
  );
}

// PR #40 review item 1: the window of year-end accruals a subsequent journal may reverse — shown and editable (default 31)
function AccrualWindow({ base, check, J, done, onSaved }) {
  const [n, setN] = useState("");
  const days = check?.accrual_days ?? 31;
  const save = async () => {
    try { await api.patch(base, { journals_accrual_days: Number(n) }); setN(""); onSaved && onSaved(); }
    catch (e) { toast.error(toErrMsg(e)); }
  };
  return (
    <div className="mt-2 space-y-1 text-xs" data-testid="surl-j-accruals">
      <p className="text-muted-foreground">
        {J.accrualWindow.replace("{n}", String(days)).replace("{from}", fmtDate(check?.accruals_from || ""))}
      </p>
      <div className="flex items-center gap-2">
        <label>{J.accrualDays}</label>
        <input type="number" min={0} max={366} value={n === "" ? days : n} disabled={done} onChange={(e) => setN(e.target.value)}
               className="w-20 rounded border px-1 py-0.5 dark:bg-slate-900" data-testid="surl-j-accrual-days" />
        {n !== "" && Number(n) !== days && (
          <button type="button" className="rounded border px-2 py-0.5" onClick={save} data-testid="surl-j-accrual-save">{J.saveReason}</button>
        )}
      </div>
      {!!check?.lines_not_checked && (
        <p className="text-amber-700" data-testid="surl-j-not-checked">{J.notChecked.replace("{n}", fmtCount(check.lines_not_checked))}</p>
      )}
    </div>
  );
}

// Step 11d · add-on: voucher, date, lines, accounts, amount, narration, relates, match, result, omitted, document, misstatement
const J_COL_W = [120, 100, 90, 220, 120, 240, 130, 140, 130, 110, 120, 130];
const J_COL_MIN = [90, 96, 64, 120, 100, 120, 96, 96, 96, 90, 90, 100];

export default function SurlJournalsTab({ engId, code, data, J, done, refresh, mf, openLink }) {
  const [busy, setBusy] = useState("");
  const jCols = useResizableColumns({ storageKey: "surl-journals", defaults: J_COL_W, mins: J_COL_MIN });
  const [onlyCand, setOnlyCand] = useState(true);
  const jr = data?.worksheet?.journals || null;
  const vouchers = asArray(jr?.vouchers);
  const shown = onlyCand ? vouchers.filter((v) => v.eval?.selected) : vouchers;
  const base = `/engagements/${engId}/surl/${code}`;
  const run = async (key, fn) => {
    setBusy(key);
    // PR 45 review · M7: a coded refusal with Arabic text (e.g. SURL_FY1_NO_LINES) shows it on the Arabic screen
    try { await fn(); await refresh(); } catch (e) { if (!(mf && mf.apply(e))) toast.error((isAr && e?.response?.data?.detail_ar) || toErrMsg(e)); } finally { setBusy(""); }
  };
  // Batch 31 · item 31: a file with no journal line (route 1's listing, missing columns, nothing in the window) is refused —
  // the text in the screen's language only, inline and on the job path; the vouchers already there are kept
  const isAr = J === JTXT.ar;
  const upload = (file) => file && run("upload", async () => {
    const fd = new FormData(); fd.append("file", file);
    let r;
    try { r = await api.post(`${base}/journals-upload`, fd, { _silent: true }); }
    catch (e) {
      const d = e?.response?.data || {};
      if (d.error_code === "SURL_UPLOAD_NO_ROWS") { toast.error((isAr && d.detail_ar) || d.detail); return; }
      throw e;
    }
    if (r.data?.mode === "job") {
      for (let i = 0; i < 600; i++) {
        await new Promise((res) => setTimeout(res, 1000));
        const j = await api.get(`/jobs/${r.data.job_id}`, { _silent: true });
        if (["done", "failed"].includes(j.data.status)) {
          if (j.data.status === "failed") toast.error(jobErrorText(j.data, isAr ? "ar" : "en", null, "failed"));
          break;
        }
      }
    }
  });
  const patch = (rowId, body) => run(rowId, async () => {
    try { await api.patch(`${base}/journals/${rowId}`, body); mf && mf.clear(`part_amount.${rowId}`); }
    catch (e) {
      const list = asArray(e?.response?.data?.missing_fields).map((m) => ({ ...m, field: `${m.field}.${rowId}` }));
      if (mf && mf.apply({ missing_fields: list })) return;
      throw e;
    }
  });
  // Batch 28 · Follow-up 1 · F2 (b): "Relates to the period?" and the part amount are row drafts — one Save / Cancel bar under
  // the table; Save sends only the changed vouchers, each with its own PATCH; a refused row keeps its draft, its error and
  // its red field. Accept / Reject of a proposed match, Same item link / unlink, Send to Misstatements, documents, the upload
  // and the FY+1 read stay immediate actions.
  const td = useTableDraft({
    rows: vouchers, getId: (v) => v.row_id, reload: refresh,
    saveRow: async (rowId, body) => {
      try { await api.patch(`${base}/journals/${rowId}`, body); mf && mf.clear(`part_amount.${rowId}`); }
      catch (e) {
        const list = asArray(e?.response?.data?.missing_fields).map((m) => ({ ...m, field: `${m.field}.${rowId}` }));
        if (mf) mf.apply({ missing_fields: list });
        throw e;
      }
    },
  });
  const lang = J === JTXT.ar ? "ar" : "en";
  const send = (rowId) => run(rowId, async () => {
    if (!(await saveRowFirst(td, rowId, lang))) return;      // STEP 11d · A9 (find 30): the changed row is saved first
    await api.post(`${base}/journals/${rowId}/send-to-misstatements`).catch(async (e) => {
      // Batch 29 · M65: already in Misstatements from another worksheet → offer the link instead of a second row
      if (!(await offerLink(e, { engId, target: { type: "surl_journal", code, row_id: rowId }, lang }))) throw e;
    });
  });
  const r1rows = asArray(data?.worksheet?.population?.rows).filter((r) => ["omitted", "part_omitted"].includes(r.eval?.result) && r.eval?.omitted_amount > 0);
  const link = (rowId, r1) => run(rowId, () => api.post(`${base}/journals/${rowId}/same-as`, { route1_row_id: r1 }));
  const unlink = (rowId) => run(rowId, () => api.delete(`${base}/journals/${rowId}/same-as`));
  return (
    <div className="space-y-3" data-testid="surl-journals">
      <div className="rounded border p-3" data-testid="surl-j-upload">
        <label className="block font-medium">{J.upload}</label>
        <p className="mt-1 text-xs text-muted-foreground">{J.uploadCols}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <input type="file" accept=".xlsx,.csv" disabled={done || busy === "upload"} className="text-xs" data-testid="surl-j-upload-input"
                 onChange={(e) => upload(e.target.files?.[0])} />
          <button type="button" disabled={done || !!busy} className="rounded border px-2 py-1 text-xs" data-testid="surl-j-fy1"
                  onClick={() => run("fy1", async () => {
                    const r = await api.post(`${base}/journals-from-fy1`);
                    if (r.data?.mode === "job") {        // over 5,000 lines: the background job (PR #40 review)
                      const j = await waitForJob(r.data.job_id, { intervalMs: 1000 });
                      if (j?.status === "failed") toast.error(jobErrorText(j, isAr ? "ar" : "en", null, "failed"));
                    }
                  })}>{J.fy1}</button>
        </div>
        {jr && <p className="mt-2 text-xs" data-testid="surl-j-info">
          {jr.source === "fy1" ? J.fy1Done : J.uploaded}: {jr.filename} · {fmtDate(jr.uploaded_at)} · {countsLine(jr, J)}
        </p>}
        {jr && <AccrualWindow base={base} check={data?.journals_check} J={J} done={done} onSaved={refresh} />}
        <div className="mt-2"><RouteToggle engId={engId} code={code} route="journals" state={data?.routes?.journals} J={J} done={done} onSaved={refresh} mf={mf} /></div>
      </div>
      {!vouchers.length ? <p className="text-muted-foreground" data-testid="surl-j-empty">{J.noRows}</p> : (<>
        <button type="button" className="text-xs underline" data-testid="surl-j-filter" onClick={() => setOnlyCand((x) => !x)}>
          {onlyCand ? J.all : J.candidatesOnly}</button>
        <div className="overflow-x-auto rounded border">
          <ResizableTable cols={jCols} className="w-full text-xs" data-testid="surl-j-rows">
            <thead className="bg-muted/50"><tr>{Object.keys(J.cols).map((k, i) => <ResizableTh key={k} index={i} className="px-2 py-1 text-start font-medium">{J.cols[k]}</ResizableTh>)}</tr></thead>
            <tbody>{shown.map((v) => <VoucherRow key={v.row_id} v={v} J={J} done={done} busy={busy === v.row_id} patch={patch} send={send}
                                                 openLink={() => openLink({ ...v, _source: "journals" })} mf={mf}
                                                 r1rows={r1rows} link={link} unlink={unlink} td={td} engId={engId} code={code} refresh={refresh} />)}</tbody>
          </ResizableTable>
          <TableSaveBar draft={td} lang={lang} disabled={done} testid="surl-j-table-save" />
        </div>
      </>)}
      <Totals t={data?.journal_totals || {}} title={J.totals} J={J} testid="surl-j-totals" />
      <Totals t={data?.combined_totals || {}} title={J.combined} J={J} testid="surl-combined-totals" />
    </div>
  );
}
